// tslint:disable:no-magic-numbers
import { Injectable } from '@angular/core';
import * as StackTrace from 'stacktrace-js';
import { Store } from '@ngrx/store';
import { getStackdriverConfig } from './config/config.selectors';
import {
  filter,
  distinctUntilChanged,
  tap
} from 'rxjs/operators';
import { DxUser } from '@digitaix/types';
import { getUser } from './authentication/selectors/auth.selectors';
import { NGXLogger } from 'ngx-logger';

function registerHandlers(reporter) {
  // Register as global error handler if requested
  // tslint:disable-next-line:no-empty
  const noop = () => {};
  if (reporter.reportUncaughtExceptions) {
    const oldErrorHandler = window.onerror || noop;

    window.onerror = (message, source, lineno, colno, error) => {
      if (error) {
        reporter.report(error)
                .catch(noop);
      }
      oldErrorHandler(message, source, lineno, colno, error);
      return true;
    };
  }
  if (reporter.reportUnhandledPromiseRejections) {
    const oldPromiseRejectionHandler = window.onunhandledrejection || noop;

    window.onunhandledrejection = (promiseRejectionEvent) => {
      if (promiseRejectionEvent) {
        reporter.report(promiseRejectionEvent.reason)
                .catch(noop);
      }
      // @ts-ignore
      oldPromiseRejectionHandler(promiseRejectionEvent.reason);
      return true;
    };
  }
}

function resolveError(err, firstFrameIndex) {
  // This will use sourcemaps and normalize the stack frames
  return StackTrace
    .fromError(err)
    .then((stack) => {
      const lines = [ err.toString() ];
      // Reconstruct to a JS stackframe as expected by Error Reporting parsers.
      for (let s = firstFrameIndex; s < stack.length; s++) {
        // Cannot use stack[s].source as it is not populated from source maps.
        lines.push([
          '    at ',
          // If a function name is not available '<anonymous>' will be used.
          stack[ s ].getFunctionName() || '<anonymous>', ' (',
          stack[ s ].getFileName(), ':',
          stack[ s ].getLineNumber(), ':',
          stack[ s ].getColumnNumber(), ')',
        ].join(''));
      }
      return lines.join('\n');
      // tslint:disable-next-line:arrow-return-shorthand
    }, (reason) => {
      // Failure to extract stacktrace
      return [
        'Error extracting stack trace: ', reason, '\n',
        err.toString(), '\n',
        '    (', err.file, ':', err.line, ':', err.column, ')',
      ].join('');
    });
}

function sendErrorPayload(url, payload) {
  const xhr = new XMLHttpRequest();
  xhr.open('POST', url, true);
  xhr.setRequestHeader('Content-Type', 'application/json; charset=UTF-8');

  return new Promise((resolve, reject) => {
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        const code = xhr.status;

        if (code >= 200 && code < 300) {
          resolve({ message: payload.message });
        } else {
          const condition = code ? `${code} http response` : 'network error';
          reject(new Error(`${condition} on stackdriver report`));
        }
      }
    };
    xhr.send(JSON.stringify(payload));
  });
}

/**
 * URL endpoint of the Stackdriver Error Reporting report API.
 */
const baseAPIUrl = 'https://clouderrorreporting.googleapis.com/v1beta1/projects/';

@Injectable({
  providedIn: 'root',
})
export class StackdriverErrorReporterService {
  private apiKey: string;
  private context: any = {};
  private disabled: boolean = true;
  private projectId: string;
  private reportUncaughtExceptions: boolean;
  private reportUnhandledPromiseRejections: boolean;
  private serviceContext: { service: any | string, version?: string };
  private targetUrl: string;

  constructor(
    public store$: Store<any>,
    public logger: NGXLogger,
  ) {
    this.store$.select(getStackdriverConfig).pipe(
      filter(Boolean),
      filter(config => !!config.key && !!config.projectId),
      distinctUntilChanged((a, b) => a.key === b.key && a.projectId === b.projectId),
      tap(config => this.start({
        key: config.key,
        projectId: config.projectId,
      })),
      tap(() => this.disabled = false),
      tap(() => this.logger.info('Logger is enabled')),
    ).subscribe();
    this.store$.select(getUser).pipe(
      filter(Boolean),
      distinctUntilChanged((a: DxUser, b: DxUser) => a.id === b.id),
      tap(user => this.setUser(user.id)),
    ).subscribe();
  }

  /**
   * Report an error to the Stackdriver Error Reporting API
   * @param err - The Error object or message string to report.
   * @param options - Configuration for this report.
   * @param [options.skipLocalFrames=1] - Omit number of frames if creating stack.
   * @returns A promise that completes when the report has been sent.
   */
  public report(err: Error | string, options: any = {}): Promise<any> {

    if (this.disabled) {
      return Promise.resolve(null);
    }
    if (!err) {
      return Promise.reject(new Error('no error to report'));
    }

    const payload: any = {
      serviceContext: this.serviceContext,
      context:        {
        ...this.context,
        httpRequest: {
          userAgent: window.navigator.userAgent,
          url:       window.location.href,
        },
      },
    };

    let firstFrameIndex = 0;
    if (typeof err === 'string' || err instanceof String) {
      // Transform the message in an error, use try/catch to make sure the stacktrace is populated.
      try {
        throw new Error(err as any);
      } catch (e) {
        err = e;
      }
      // the first frame when using report() is always this library
      firstFrameIndex = options.skipLocalFrames || 1;
    }

    const reportUrl = this.targetUrl || (
      `${baseAPIUrl}${this.projectId}/events:report?key=${this.apiKey}`);

    return resolveError(err, firstFrameIndex)
      .then((message) => {
        payload.message = message;
        return sendErrorPayload(reportUrl, payload);
      });

  }

  /**
   * Set the user for the current context.
   *
   * @param user - the unique identifier of the user (can be ID, email or custom token) or `undefined` if not logged in.
   */
  public setUser(user: string) {
    this.context.user = user;
  }

  /**
   * Initialize the StackdriverErrorReporter object.
   * @param config - the init configuration.
   * @param [config.context={}] - the context in which the error occurred.
   * @param [config.context.user] - the user who caused or was affected by the error.
   * @param config.key - the API key to use to call the API.
   * @param config.projectId - the Google Cloud Platform project ID to report errors to.
   * @param [config.service=web] - service identifier.
   * @param [config.version] - version identifier.
   * @param [config.reportUncaughtExceptions=true] - Set to false to stop reporting unhandled exceptions.
   * @param [config.disabled=false] - Set to true to not report errors when calling report(), this can be used when developping locally.
   */
  public start(config: any) {
    if (!config.key && !config.targetUrl) {
      throw new Error('Cannot initialize: No API key or target url provided.');
    }
    if (!config.projectId && !config.targetUrl) {
      throw new Error('Cannot initialize: No project ID or target url provided.');
    }

    this.apiKey         = config.key;
    this.projectId      = config.projectId;
    this.targetUrl      = config.targetUrl;
    this.context        = config.context || {};
    this.serviceContext = { service: config.service || 'web' };
    if (config.version) {
      this.serviceContext.version = config.version;
    }
    this.reportUncaughtExceptions         = config.reportUncaughtExceptions !== false;
    this.reportUnhandledPromiseRejections = config.reportUnhandledPromiseRejections !== false;
    this.disabled                         = !!config.disabled;

    registerHandlers(this);
  }

}
